// ===========================================
// COMPONENTE: ImpuestoDetalleDialog
// ===========================================
// Sheet lateral con detalle de un impuesto del catálogo.
// Muestra: header con badge, clientes asignados
// y vencimientos recientes.

import { format, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import { Users, Clock, AlertTriangle, CheckCircle } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ImpuestoBadge } from '@/components/ui/impuesto-badge'
import { useImpuestoDetalle } from '@/hooks/useImpuestosPage'
import type { CatalogoConMetricas } from '@/hooks/useImpuestosPage'
import {
  CATEGORIAS_IMPUESTO_CATALOGO,
  type ColorImpuesto,
  type CategoriaImpuestoCatalogo,
} from '@/types/impuestos'

// ===========================================
// Props
// ===========================================

interface ImpuestoDetalleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  impuesto: CatalogoConMetricas | null
  onMarcarPresentado?: (vencimiento: { id: string; tipo_impuesto: string; cliente_nombre?: string; periodo: string; fecha_vencimiento: string }) => void
}

// ===========================================
// Helpers
// ===========================================

/** Formatea CUIT como XX-XXXXXXXX-X */
function formatCuit(cuit: string | null | undefined): string {
  if (!cuit) return '-'
  const digits = cuit.replace(/\D/g, '')
  if (digits.length !== 11) return cuit
  return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`
}

/** Formatea fecha ISO como "15 ene. 2025" */
function formatFecha(fecha: string | null | undefined): string {
  if (!fecha) return '-'
  try {
    return format(parseISO(fecha), "d MMM. yyyy", { locale: es })
  } catch {
    return fecha
  }
}

/** Formatea período como "202501" → "Ene. 2025" */
function formatPeriodo(periodo: string | null | undefined): string {
  if (!periodo) return '-'
  // Soporta formatos: "202501", "2025-01", "2025-01-01"
  const clean = periodo.replace(/\D/g, '')
  if (clean.length < 6) return periodo
  const year = clean.slice(0, 4)
  const month = parseInt(clean.slice(4, 6), 10)
  try {
    const date = new Date(parseInt(year), month - 1, 1)
    return format(date, "MMM. yyyy", { locale: es })
  } catch {
    return periodo
  }
}

// ===========================================
// Sub-componente: Skeleton de carga
// ===========================================

function LoadingSkeleton() {
  return (
    <div className="space-y-6 py-4">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />
        ))}
      </div>
      <div className="h-px bg-border" />
      {/* Clientes */}
      <div className="space-y-3">
        <div className="h-4 w-32 bg-muted animate-pulse rounded" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-12 bg-muted animate-pulse rounded-lg" />
        ))}
      </div>
      <div className="h-px bg-border" />
      {/* Vencimientos */}
      <div className="space-y-3">
        <div className="h-4 w-40 bg-muted animate-pulse rounded" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-10 bg-muted animate-pulse rounded" />
        ))}
      </div>
    </div>
  )
}

// ===========================================
// Sub-componente: Badge de estado vencimiento
// ===========================================

type EstadoVencimiento = string | null | undefined

function EstadoBadge({ estado, presentado }: { estado: EstadoVencimiento; presentado: boolean | null }) {
  if (presentado) {
    return (
      <Badge variant="default" className="bg-green-600 hover:bg-green-600 text-white text-xs">
        Presentado
      </Badge>
    )
  }
  if (estado === 'vencido') {
    return (
      <Badge variant="destructive" className="text-xs">
        Vencido
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className="text-xs">
      Pendiente
    </Badge>
  )
}

// ===========================================
// Componente principal
// ===========================================

export function ImpuestoDetalleDialog({
  open,
  onOpenChange,
  impuesto,
  onMarcarPresentado,
}: ImpuestoDetalleDialogProps) {
  const { data: detalle, isLoading } = useImpuestoDetalle(impuesto?.id ?? null)

  const colorSeguro = (impuesto?.color ?? 'gray') as ColorImpuesto
  const categoriaLabel = impuesto?.categoria
    ? (CATEGORIAS_IMPUESTO_CATALOGO[impuesto.categoria as CategoriaImpuestoCatalogo] ?? impuesto.categoria)
    : null

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[500px] sm:max-w-[500px] flex flex-col p-0">
        {/* ---- Header ---- */}
        {impuesto ? (
          <SheetHeader className="px-6 pt-6 pb-4 border-b">
            <div className="flex items-start gap-3">
              <ImpuestoBadge
                nombre={impuesto.nombre_corto ?? impuesto.codigo}
                color={colorSeguro}
                size="md"
                className="mt-0.5 shrink-0"
              />
              <div className="min-w-0">
                <SheetTitle className="text-left leading-tight">{impuesto.nombre}</SheetTitle>
                <SheetDescription className="text-left mt-0.5 flex items-center gap-1.5 flex-wrap">
                  {categoriaLabel && <span>{categoriaLabel}</span>}
                  {impuesto.jurisdiccion && (
                    <>
                      <span className="text-muted-foreground/40">·</span>
                      <span>{impuesto.jurisdiccion}</span>
                    </>
                  )}
                  <span className="text-muted-foreground/40">·</span>
                  <span className="font-mono text-xs">{impuesto.codigo}</span>
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>
        ) : (
          // Header vacío con accesibilidad
          <SheetHeader className="sr-only">
            <SheetTitle>Detalle de impuesto</SheetTitle>
          </SheetHeader>
        )}

        {/* ---- Cuerpo scrolleable ---- */}
        <ScrollArea className="flex-1">
          <div className="px-6 py-4 space-y-6">
            {!impuesto ? null : isLoading ? (
              <LoadingSkeleton />
            ) : (
              <>
                {/* ---- Sección 1: Mini cards de stats ---- */}
                <div className="grid grid-cols-3 gap-3">
                  {/* Clientes asignados */}
                  <div className="rounded-lg border bg-card p-3 text-center">
                    <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                      <Users className="h-3.5 w-3.5" />
                      <span className="text-xs">Clientes</span>
                    </div>
                    <p className="text-2xl font-bold">{impuesto.clientes_asignados}</p>
                  </div>

                  {/* Pendientes */}
                  <div className="rounded-lg border bg-card p-3 text-center">
                    <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                      <Clock className="h-3.5 w-3.5" />
                      <span className="text-xs">Pendientes</span>
                    </div>
                    <p className="text-2xl font-bold text-amber-600">
                      {impuesto.vencimientos_pendientes}
                    </p>
                  </div>

                  {/* Vencidos */}
                  <div className="rounded-lg border bg-card p-3 text-center">
                    <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      <span className="text-xs">Vencidos</span>
                    </div>
                    <p className="text-2xl font-bold text-destructive">
                      {impuesto.vencimientos_vencidos}
                    </p>
                  </div>
                </div>

                <Separator />

                {/* ---- Sección 2: Clientes Asignados ---- */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Clientes Asignados
                    {detalle && (
                      <span className="text-muted-foreground font-normal">
                        ({detalle.clientes.length})
                      </span>
                    )}
                  </h3>

                  {!detalle || detalle.clientes.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-3 text-center">
                      No hay clientes asignados a este impuesto
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {detalle.clientes.map((cliente, idx) => (
                        <div
                          key={`${cliente.impuesto_id}-${idx}`}
                          className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <span className="text-sm font-medium text-primary">
                                {(cliente.nombre ?? '?').charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">
                                {cliente.nombre ?? 'Sin nombre'}
                              </p>
                              <p className="text-xs text-muted-foreground font-mono">
                                {formatCuit(cliente.cuit)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap shrink-0 ml-2">
                            {cliente.numero_inscripcion && (
                              <span className="text-xs text-muted-foreground">
                                Nro. {cliente.numero_inscripcion}
                              </span>
                            )}
                            {cliente.alicuota != null && (
                              <span className="text-xs text-muted-foreground">
                                Alic. {cliente.alicuota}%
                              </span>
                            )}
                            {cliente.regimen && (
                              <span className="text-xs text-muted-foreground">
                                Rég. {cliente.regimen}
                              </span>
                            )}
                            {cliente.fecha_inscripcion && (
                              <span className="text-xs text-muted-foreground">
                                Desde {formatFecha(cliente.fecha_inscripcion)}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Separator />

                {/* ---- Sección 3: Vencimientos Recientes ---- */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Vencimientos Recientes
                  </h3>

                  {!detalle || detalle.vencimientos.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-3 text-center">
                      No hay vencimientos registrados
                    </p>
                  ) : (
                    <div className="rounded-md border overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-muted/50 border-b">
                            <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">
                              Período
                            </th>
                            <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">
                              Vencimiento
                            </th>
                            <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">
                              Estado
                            </th>
                            {onMarcarPresentado && (
                              <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground w-24">
                                Accion
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {detalle.vencimientos.map((v) => (
                            <tr key={v.id} className="border-b last:border-0 hover:bg-muted/30">
                              <td className="px-3 py-2 font-mono text-xs">
                                {formatPeriodo(v.periodo)}
                              </td>
                              <td className="px-3 py-2 text-xs text-muted-foreground">
                                {formatFecha(v.fecha_vencimiento)}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <EstadoBadge
                                  estado={v.estado_efectivo}
                                  presentado={v.presentado}
                                />
                              </td>
                              {onMarcarPresentado && (
                                <td className="px-3 py-2 text-right">
                                  {!v.presentado && v.estado_efectivo !== 'presentado' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 text-xs text-green-700 hover:text-green-800 hover:bg-green-50"
                                      onClick={() => onMarcarPresentado({
                                        id: v.id,
                                        tipo_impuesto: impuesto?.nombre ?? '',
                                        periodo: v.periodo,
                                        fecha_vencimiento: v.fecha_vencimiento,
                                      })}
                                    >
                                      <CheckCircle className="h-3.5 w-3.5 mr-1" />
                                      Presentar
                                    </Button>
                                  )}
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}
